import type { Monaco } from "@monaco-editor/react";
import type { editor, Position } from "monaco-editor";
import type { KernelState } from "@/kernel/KernelContext";

/**
 * Enregistre les fournisseurs Monaco pour Python, alimentés par Jedi (dans le worker
 * Pyodide) :
 *   - survol d'un symbole → signature + docstring (paramètres attendus) ;
 *   - Cmd/Ctrl-clic sur un appel → aller à la définition (intra-fichier uniquement).
 *
 * Jedi : ligne 1-based, colonne 0-based. Monaco : ligne 1-based, colonne 1-based.
 * On convertit donc `column - 1` à l'envoi et `column + 1` au retour.
 *
 * Idempotent (drapeau module) : les fournisseurs sont globaux par langage, on ne les
 * enregistre qu'une fois. Les méthodes du kernel sont stables (useCallback), donc la
 * capture initiale reste valide.
 */
let registered = false;

export function registerPythonProviders(monaco: Monaco, kernel: KernelState) {
  if (registered) return;
  registered = true;

  monaco.languages.registerHoverProvider("python", {
    async provideHover(model: editor.ITextModel, position: Position) {
      const info = await kernel.signature(model.getValue(), position.lineNumber, position.column - 1);
      if (!info || !info.doc) return null;

      // Première section (jusqu'à une ligne vide) = signature → bloc de code ;
      // le reste = description.
      const doc = info.doc.trim();
      const splitAt = doc.indexOf("\n\n");
      const sig = splitAt === -1 ? doc : doc.slice(0, splitAt);
      const rest = splitAt === -1 ? "" : doc.slice(splitAt + 2).trim();

      const contents = [{ value: "```python\n" + sig + "\n```" }];
      if (rest) contents.push({ value: rest.length > 600 ? rest.slice(0, 600) + "…" : rest });
      return { contents };
    },
  });

  monaco.languages.registerDefinitionProvider("python", {
    async provideDefinition(model: editor.ITextModel, position: Position) {
      const loc = await kernel.goto(model.getValue(), position.lineNumber, position.column - 1);
      if (!loc) return null;
      const col = loc.column + 1;
      return {
        uri: model.uri,
        range: new monaco.Range(loc.line, col, loc.line, col),
      };
    },
  });
}
